import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, Send, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';
import { GoogleBusinessSection } from '../components/GoogleBusiness';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formError = (err, t) => t(err.response?.status === 429 ? 'form.rateLimited' : err.response?.status === 422 ? 'form.invalid' : 'form.error');

export default function ContactPage() {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '', preferred_language: '' });

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.message) {
      toast.error(language === 'de' ? 'Bitte alle Pflichtfelder ausfüllen' : language === 'en' ? 'Please fill in all required fields' : 'Por favor, complete todos los campos obligatorios');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success(t('contact.success'));
      setForm({ name: '', email: '', phone: '', company: '', message: '', preferred_language: '' });
    } catch (err) {
      toast.error(formError(err, t));
    }
    setLoading(false);
  };

  const tz = settings.opening_hours_timezone || 'Europe/Berlin';
  const monFri = settings.opening_hours_mon_fri || '08:00 - 17:00';
  const sat = settings.opening_hours_sat || 'geschlossen';
  const sun = settings.opening_hours_sun || 'geschlossen';
  const phoneVal = settings.contact_phone || t('contact.phoneVal');
  const closedLabel = (val) => {
    if (!val) return val;
    const low = val.toLowerCase();
    if (low === 'geschlossen') return language === 'en' ? 'closed' : language === 'es' ? 'cerrado' : 'geschlossen';
    return val;
  };

  return (
    <div data-testid="contact-page">
      <SEO page="contact" />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-[#3b8dff] text-sm font-semibold uppercase tracking-wider mb-4">{t('contact.tag')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6" data-testid="contact-title">{t('contact.title')}</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-32 blueprint-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-[#0F172A]">ITCS Industrial</h2>
              <p className="text-slate-600 text-sm">Trade & Construction Systems GmbH</p>
              <div className="space-y-5">
                {[
                  { icon: Mail, label: t('contact.email'), value: t('contact.emailVal'), href: 'mailto:info@itcs-global.com' },
                  { icon: Phone, label: t('contact.phone'), value: phoneVal, href: `tel:${phoneVal.replace(/\s+/g, '')}` },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[#066ce7]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-medium text-[#0F172A] hover:text-[#066ce7] transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-sm font-medium text-[#0F172A]">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
                {/* Opening Hours */}
                <div className="flex items-start gap-4" data-testid="contact-info-hours">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-[#066ce7]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{language === 'de' ? 'Öffnungszeiten' : language === 'en' ? 'Opening hours' : 'Horario'}</p>
                    <p className="text-sm font-medium text-[#0F172A]">{language === 'de' ? 'Mo – Fr' : language === 'en' ? 'Mon – Fri' : 'Lu – Vi'}: {monFri}</p>
                    <p className="text-sm font-medium text-[#0F172A]">{language === 'de' ? 'Sa' : language === 'en' ? 'Sat' : 'Sá'}: {closedLabel(sat)}</p>
                    <p className="text-sm font-medium text-[#0F172A]">{language === 'de' ? 'So' : language === 'en' ? 'Sun' : 'Do'}: {closedLabel(sun)}</p>
                    <p className="text-xs text-slate-500 mt-1">{language === 'de' ? 'Zeitzone' : language === 'en' ? 'Timezone' : 'Zona horaria'}: {tz}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 md:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.name')} *</Label>
                    <Input value={form.name} onChange={e => updateForm('name', e.target.value)} required data-testid="contact-input-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.email')} *</Label>
                    <Input type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} required data-testid="contact-input-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.phone')}</Label>
                    <Input value={form.phone} onChange={e => updateForm('phone', e.target.value)} data-testid="contact-input-phone" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.company')} *</Label>
                    <Input value={form.company} onChange={e => updateForm('company', e.target.value)} required data-testid="contact-input-company" />
                  </div>
                </div>
                <div className="mb-6 space-y-5">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.message')} *</Label>
                    <Textarea value={form.message} onChange={e => updateForm('message', e.target.value)} rows={5} required data-testid="contact-input-message" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-2 block">{t('contact.language')}</Label>
                    <Select value={form.preferred_language} onValueChange={v => updateForm('preferred_language', v)}>
                      <SelectTrigger data-testid="contact-select-language">
                        <SelectValue placeholder={t('contact.language')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="de" data-testid="lang-de">{t('contact.language.de')}</SelectItem>
                        <SelectItem value="en" data-testid="lang-en">{t('contact.language.en')}</SelectItem>
                        <SelectItem value="es" data-testid="lang-es">{t('contact.language.es')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="bg-[#066ce7] hover:bg-[#005bb5] text-white px-8 py-3 font-semibold w-full md:w-auto" data-testid="contact-submit-btn">
                  {loading ? '...' : t('contact.send')} <Send className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <GoogleBusinessSection />
    </div>
  );
}
